<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="align-center">
      <v-col cols="12" md="8">
        <div class="d-flex flex-column">
          <h2 class="mb-1 font-weight-bold">Gestión de CNA</h2>
          <div class="subtitle grey--text text--darken-1">
            Administra {{ totalSolicitudes }} solicitudes y {{ totalOficios }} oficios
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right mt-3 mt-md-0">
        <v-btn v-if="usuario_log.rol === 'Administrador'" color="primary" dark @click="nuevaSolicitud" class="elevation-1">
          <v-icon left>add</v-icon>
          Nueva solicitud
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-card class="mt-4 filter-card" outlined>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="q"
              dense
              outlined
              clearable
              hide-details
              prepend-inner-icon="search"
              label="Buscar por folio"
            />
          </v-col>

          <v-col cols="6" md="3">
            <v-select
              v-model="tipo_oficio"
              :items="tipoOficioOptions"
              dense
              outlined
              hide-details
              clearable
              label="Tipo de oficio"
              item-text="text"
              item-value="value"
            />
          </v-col>

          <v-col cols="6" md="3">
            <v-select
              v-model="status"
              :items="statusOptions"
              dense
              outlined
              hide-details
              clearable
              label="Estatus"
              item-text="text"
              item-value="value"
            />
          </v-col>

          <v-col cols="6" md="3">
            <v-menu
              v-model="menuFecha"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="320px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fechaTexto"
                  dense
                  outlined
                  hide-details
                  readonly
                  clearable
                  @click:clear="limpiarFecha"
                  v-bind="attrs"
                  v-on="on"
                  label="Rango de fechas"
                  prepend-inner-icon="event"
                ></v-text-field>
              </template>
              <v-card>
                <v-date-picker
                  v-model="rangoFecha"
                  range
                  @change="aplicarRangoFecha"
                ></v-date-picker>
                <v-card-actions class="justify-end">
                  <v-btn text small @click="limpiarFecha">Limpiar</v-btn>
                  <v-btn color="primary" text small @click="menuFecha = false"
                    >Aceptar</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <div class="caption grey--text text--darken-1">
          Mostrando {{ showing }} de {{ totalFiltrado }} de
          {{ totalSolicitudes }} solicitudes
        </div>
      </v-card-text>
    </v-card>

    <!-- Dialogo: Nueva solicitud CNA -->
    <v-dialog v-model="dialogCrear" max-width="720">
      <v-card>
        <v-toolbar color="primary" dark>
          <v-toolbar-title>Nueva solicitud CNA</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogCrear = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-6">
          <v-form ref="formCrear" lazy-validation>
            <v-row dense>
              <v-col cols="12">
                <div class="text-subtitle-2 mb-1">Datos de referencia</div>
                <v-autocomplete
                  v-model="form.solicitud"
                  :items="solicitudes"
                  dense
                  outlined
                  clearable
                  hide-details
                  label="Solicitud única"
                  :rules="[rules.required]"
                  item-text="text"
                  item-value="value"
                  @change="obtenerDetallesSolicitud"
                  prepend-inner-icon="assignment"
                />
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-1">Tipo</div>
                <v-btn-toggle v-model="form.tipo" mandatory dense class="mb-2">
                  <v-btn small value="Liquidado">Liquidado</v-btn>
                  <v-btn small value="Garantizado">Garantizado</v-btn>
                </v-btn-toggle>
                <div class="caption grey--text" v-if="!form.tipo">
                  Selecciona una opción
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-1">Tipo de Carga</div>
                <v-btn-toggle v-model="form.tipo_carga" mandatory dense class="mb-2">
                  <v-btn small value="General Suelta">General Suelta</v-btn>
                  <v-btn small value="Lastre">Lastre</v-btn>
                  <v-btn small value="Otro">Otro</v-btn>
                </v-btn-toggle>
                <v-text-field
                  v-if="form.tipo_carga === 'Otro'"
                  v-model="form.tipo_carga_otro"
                  label="Especifique tipo de carga"
                  dense
                  outlined
                  hide-details
                  clearable
                />
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-1">Detalle de operación</div>
                <v-text-field
                  v-model="form.puerto_destino"
                  label="Puerto de destino"
                  dense
                  outlined
                  hide-details
                  clearable
                  :rules="[rules.required]"
                />
                <v-textarea
                  class="mt-2"
                  v-model="form.realiza_operaciones_de"
                  label="Realiza operaciones de"
                  dense
                  outlined
                  hide-details
                  clearable
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-1">Uso de infraestructura</div>
                <v-row no-gutters>
                  <v-col cols="12" md="12">
                    <v-checkbox
                      v-for="opt in usoInfraestructuraOpts"
                      :key="opt"
                      v-model="form.usos"
                      :label="opt"
                      :value="opt"
                      hide-details
                      dense
                      class="my-0"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <div class="caption grey--text">Puedes seleccionar más de una opción</div>
              </v-col>

              <v-col cols="12">
                <v-sheet class="pa-3" color="grey lighten-3" rounded>
                  <div class="text-subtitle-2 mb-3">Datos de la Solicitud Única</div>
                  <v-alert v-if="bloqueado" type="error" dense outlined class="mb-3">{{
                    bloqueoMensaje
                  }}</v-alert>
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.puerto_procedencia"
                        label="Puerto de procedencia"
                        dense
                        outlined
                        hide-details
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.fecha_arribo"
                        label="Fecha de atraque"
                        dense
                        outlined
                        hide-details
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.hora_arribo"
                        label="Hora de atraque"
                        dense
                        outlined
                        hide-details
                        disabled
                      />
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.muelle"
                        label="Muelle"
                        dense
                        outlined
                        hide-details
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.fecha_zarpe"
                        label="Fecha de desatraque"
                        dense
                        outlined
                        hide-details
                        disabled
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.hora_zarpe"
                        label="Hora de desatraque"
                        dense
                        outlined
                        hide-details
                        disabled
                      />
                    </v-col>
                  </v-row>
                </v-sheet>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="dialogCrear = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            dark
            @click="guardar"
            :loading="saving"
            :disabled="bloqueado || !isValidForm || saving"
            >Guardar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- Tabla de Solicitudes con CNA -->
    <v-card class="mt-6 rounded-lg elevation-2">
      <v-data-table
        :headers="headersSolicitudes"
        :items="solicitudesTabla"
        item-key="_id"
        :loading="loading"
        dense
        loading-text="Cargando..."
        show-expand
        single-expand
        class="elevation-1 custom-table-header"
        hide-default-footer
      >
        <!-- Fecha con icono -->
        <template v-slot:item.fecha="{ item }">
          <span class="text-caption font-weight-medium">{{
            formatDateTime(item.fecha)
          }}</span>
        </template>

        <template v-slot:item.folio_solicitud="{ item }">
          <div class="d-flex flex-column">
            <!-- Folio principal -->
            <span class="text-caption font-weight-medium">
              {{ item.folio_solicitud }}
            </span>

            <!-- Embarcación debajo en gris fuerte -->
            <span class="text-caption grey--text text--darken-2">
              {{ item.embarcacion.nombre }}
            </span>
          </div>
        </template>

        <template v-slot:item.solicitante="{ item }">
          <v-icon>person </v-icon>
          <span class="text-caption grey--text text--darken-2 font-weight-medium">
            {{ item.solicitante }}
          </span>
        </template>

        <template v-slot:item.oficios="{ item }">
          <v-icon>article</v-icon>
          <span class="text-caption grey--text text--darken-2 font-weight-medium">
            {{ item.oficios }} Oficios
          </span>
        </template>

        <!-- Expansión con tabla interna -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="bg-grey lighten-5 pa-3">
            <v-card flat class="rounded-lg">
              <v-simple-table dense>
                <thead>
                  <tr class="">
                    <th class="text-left table-header">Folio CNA</th>
                    <th class="text-left table-header">Tipo de oficio</th>
                    <th class="text-left table-header">Tipo de carga</th>
                    <th class="text-left table-header">Fecha de creación</th>
                    <th class="text-left table-header">Pagado</th>
                    <th class="text-left table-header">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!item.cna || item.cna.length === 0">
                    <td colspan="6" class="grey--text text--darken-1 text-center">
                      🚫 Sin oficios registrados
                    </td>
                  </tr>
                  <tr v-for="of in item.cna" :key="of._id">
                    <td>
                      <span
                        class="text-caption font-weight-medium"
                        v-if="of.tipo === 'Liquidado'"
                      >
                        {{ `APITAB-OP-F-${of.folio_cna}-CNA` }}
                      </span>
                      <span class="text-caption font-weight-medium" v-else>
                        <div v-if="of.pagado">
                          {{ `APITAB-OP-F-${of.folio_cna}-CNA-B` }}
                        </div>
                        <div v-else>
                          {{ `APITAB-OP-F-${of.folio_cna}-CNA-A` }}
                        </div>
                      </span>
                    </td>
                    <td class="text-caption">{{ of.tipo || "N/D" }}</td>
                    <td class="text-caption">{{ of.tipo_carga || "N/D" }}</td>
                    <td class="text-caption">{{ formatDateTime(of.createdAt) }}</td>
                    <td>
                      <v-chip
                        label
                        small
                        :color="of.pagado ? 'green lighten-4' : 'red lighten-4'"
                        :text-color="of.pagado ? 'green darken-3' : 'red darken-3'"
                      >
                        {{ of.pagado ? "Pagado" : "No pagado" }}
                      </v-chip>
                    </td>

                    <td>
                      <v-btn
                        @click="imprimirCna(of, item)"
                        class="mx-2"
                        dark
                        x-small
                        color="primary"
                      >
                        <v-icon small dark> download </v-icon>
                      </v-btn>
                      <v-btn
                        @click="verDilaogConfirmPago(of._id)"
                        class="mx-2"
                        dark
                        v-if="!of.pagado"
                        x-small
                        color="primary"
                      >
                        <v-icon small dark> attach_money </v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </td>
        </template>
      </v-data-table>

      <!-- Paginación personalizada -->
      <v-card-actions class="justify-space-between align-center pa-4">
        <div class="text-caption grey--text">
          Mostrando {{ (page - 1) * limit + 1 }} -
          {{ Math.min(page * limit, totalFiltrado) }} de {{ totalFiltrado }} registros
        </div>
        <v-pagination
          v-model="page"
          :length="pagination.totalPages"
          :total-visible="7"
          @input="cambiarPagina"
        ></v-pagination>
        <v-select
          v-model="limit"
          :items="[5, 10, 25, 50, 100]"
          label="Registros por página"
          dense
          outlined
          hide-details
          style="max-width: 180px"
          @change="cambiarLimite"
        ></v-select>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="dialogConfirmarPago" max-width="500">
      <v-card>
        <v-card-title class="headline"> Confirmar Pago CNA </v-card-title>

        <v-card-text>
          <p>
            ¿Estás seguro de que la agencia ya realizó el pago de
            <strong>CNA</strong>?
          </p>
          <p class="red--text font-weight-bold">
            ⚠️ Una vez confirmado ya no se podrá cambiar.
          </p>

          <!-- Checkbox adicional de confirmación -->
          <v-checkbox
            v-model="confirmacionExtra"
            label="Sí, confirmo que el pago ya fue realizado"
            color="green darken-2"
          ></v-checkbox>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="dialogConfirmarPago = false">Cancelar</v-btn>

          <v-btn
            color="green darken-2"
            dark
            :disabled="!confirmacionExtra"
            @click="confirmarPago"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  getFoliosSolicitudes,
  detallesSolicitudCna,
  addCna,
  solicitudesConCna,
  actualizarPagoCna,
} from "../../api/endpoints";
import { oficio_cna } from "../agencias/pdf/oficio_cna";

export default {
  name: "SolicitudNoAdeudo",
  data() {
    return {
      // header counts (placeholder values; reemplazar con datos reales)
      totalSolicitudes: 0,
      totalOficios: 0,
      loading: true,
      dialogConfirmarPago: false,
      confirmacionExtra: false,
      _idCna: "",
      usuario_log: [],
      // filtros
      q: "",
      fecha: null,
      rangoFecha: [],
      fechaInicio: null,
      fechaFin: null,
      menuFecha: false,
      status: "",
      tipo_oficio: "",
      searchTimeout: null,
      filterTimeout: null,
      statusOptions: [
        { text: "Todos", value: "" },
        { text: "Pagado", value: "pagado" },
        { text: "Pendiente de Pago", value: "pendiente" },
      ],
      tipoOficioOptions: [
        { text: "Todos", value: "" },
        { text: "Liquidado", value: "Liquidado" },
        { text: "Garantizado", value: "Garantizado" },
      ],

      // datos para paginación server-side
      totalFiltrado: 0,
      showing: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
      solicitudes: [],

      // dialogo crear
      dialogCrear: false,
      bloqueado: false,
      bloqueoMensaje: "",
      solicitudesCna: [],
      pagination: {
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
      },
      saving: false,
      snackbar: { show: false, text: "", color: "success" },
      detallesSolicitud: {},
      headersSolicitudes: [
        { text: "Folio solicitud", value: "folio_solicitud" },
        { text: "Solicitante", value: "solicitante" },
        { text: "Fecha", value: "fecha" },
        { text: "Oficios", value: "oficios" },
      ],
      form: {
        solicitud: null,
        tipo: "",
        tipo_carga: "",
        tipo_carga_otro: "",
        usos: [],
        puerto_procedencia: "",
        puerto_destino: "",
        fecha_arribo: "",
        hora_arribo: "",
        muelle: "",
        fecha_zarpe: "",
        hora_zarpe: "",
        realiza_operaciones_de: "",
      },
      usoInfraestructuraOpts: [
        "Tráfico de altura",
        "Puerto Fijo",
        "Atraque",
        "Muellaje",
        "Puerto variable",
        "Tráfico de cabotaje",
        "Pasajeros",
        "Puerto menor 500 ton",
      ],

      rules: {
        required: (v) => (!!v && (Array.isArray(v) ? v.length > 0 : true)) || "Requerido",
      },
    };
  },
  created() {
    // Aquí se pueden cargar los totales iniciales y el conteo filtrado
    // Por ahora usamos valores de ejemplo
    this.totalSolicitudes = 0;
    this.totalOficios = 0;
    this.totalFiltrado = 0;
    this.showing = 0;
    this.usuario_log = this.$store.state.usuario;
    this.obtenerFoliosSolicitud();
    this.obtenerSolicitudesConCna();
  },
  watch: {
    q() {
      this.debouncedSearch();
    },
    status() {
      this.aplicarFiltros();
    },
    fechaInicio() {
      this.debouncedApplyFilters();
    },
    fechaFin() {
      this.debouncedApplyFilters();
    },
    tipo_oficio() {
      this.aplicarFiltros();
    },
  },
  methods: {
    nuevaSolicitud() {
      this.dialogCrear = true;
    },
    onSelectSolicitud(su) {
      if (!su) {
        this.form.puerto_procedencia = "";
        this.form.fecha_arribo = "";
        this.form.hora_arribo = "";
        this.form.muelle = "";
        this.form.fecha_zarpe = "";
        this.form.hora_zarpe = "";
        return;
      }
      this.form.puerto_procedencia = su.puerto_procedencia || "";
      this.form.fecha_arribo = su.fecha_arribo || "";
      this.form.hora_arribo = su.hora_arribo || "";
      this.form.muelle = su.muelle || "";
      this.form.fecha_zarpe = su.fecha_zarpe || "";
      this.form.hora_zarpe = su.hora_zarpe || "";
    },
    async guardar() {
      if (this.bloqueado || !this.isValidForm || this.saving) return;
      try {
        this.saving = true;
        const payload = {
          ...this.form,
          fecha_atraque: this.form.fecha_arribo,
          hora_atraque: this.form.hora_arribo,
          fecha_zarpe: this.form.fecha_zarpe,
          hora_zarpe: this.form.hora_zarpe,
        };
        await addCna(payload);
        this.snackbar = {
          show: true,
          text: "CNA creado correctamente.",
          color: "success",
        };
        this.dialogCrear = false;
        this.obtenerSolicitudesConCna();

        // Opcional: resetear formulario
        // this.resetForm();
      } catch (err) {
        console.error(err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Ocurrió un error al crear el CNA";
        this.snackbar = { show: true, text: msg, color: "error" };
      } finally {
        this.saving = false;
      }
    },

    // Obtiene los folios de solicitudes disponibles para crear nuevos CNAs
    obtenerFoliosSolicitud() {
      this.loading = true;
      getFoliosSolicitudes()
        .then((e) => {
          this.solicitudes = e;
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // Obtiene las solicitudes que ya tienen CNAs asociados, con filtros y paginación
    obtenerSolicitudesConCna(filtros = {}) {
      this.loading = true;
      const params = {
        ...filtros,
        page: this.page,
        limit: this.limit,
      };

      solicitudesConCna(params)
        .then((response) => {
          this.solicitudesCna = response.data || [];
          this.pagination = response.pagination || {
            limit: 10,
            page: 1,
            total: 0,
            totalPages: 0,
          };
          this.totalFiltrado = this.pagination.total;
          this.showing = this.solicitudesCna.length;
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Aplica debounce a la búsqueda para evitar múltiples requests mientras el usuario escribe
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.aplicarFiltros();
      }, 500);
    },

    // Aplica debounce a los filtros de fecha para optimizar rendimiento
    debouncedApplyFilters() {
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }
      this.filterTimeout = setTimeout(() => {
        this.aplicarFiltros();
      }, 100);
    },

    // Construye y aplica los filtros seleccionados por el usuario
    aplicarFiltros() {
      this.page = 1; // Reset to first page when applying filters
      const filtros = {};
      if (this.q && this.q.trim()) {
        filtros.q = this.q.trim();
      }
      if (this.status) {
        filtros.status = this.status;
      }
      if (this.fechaInicio) {
        filtros.fechaInicio = this.fechaInicio;
      }
      if (this.fechaFin) {
        filtros.fechaFin = this.fechaFin;
      }
      if (this.tipo_oficio) {
        filtros.tipo_oficio = this.tipo_oficio;
      }
      this.obtenerSolicitudesConCna(filtros);
    },

    limpiarFecha() {
      this.rangoFecha = [];
      this.fechaInicio = null;
      this.fechaFin = null;
    },

    aplicarRangoFecha() {
      if (this.rangoFecha && this.rangoFecha.length > 0) {
        this.fechaInicio = this.rangoFecha[0];
        this.fechaFin =
          this.rangoFecha.length > 1 ? this.rangoFecha[1] : this.rangoFecha[0];
      }
    },

    // Obtiene los detalles de la solicitud seleccionada y valida que tenga fechas de atraque/desatraque
    obtenerDetallesSolicitud() {
      this.loading = true;
      this.bloqueado = false;
      this.bloqueoMensaje = "";
      if (!this.form.solicitud) {
        this.form.puerto_procedencia = "";
        this.form.muelle = "";
        this.form.fecha_arribo = "";
        this.form.hora_arribo = "";
        this.form.fecha_zarpe = "";
        this.form.hora_zarpe = "";
        this.loading = false;
        return;
      }
      detallesSolicitudCna(this.form.solicitud)
        .then((e) => {
          this.detallesSolicitud = e;
          const { atraque, desatraque, solicitud } = e || {};

          // Rellenar campos del bloque de Datos de la Solicitud Única
          this.form.puerto_procedencia = solicitud?.procedencia || "";
          this.form.muelle = solicitud?.muelle || "";

          if (atraque) {
            this.form.fecha_arribo = this.formatDate(atraque);
            this.form.hora_arribo = this.formatTime(atraque);
          } else {
            this.form.fecha_arribo = "";
            this.form.hora_arribo = "";
          }

          if (desatraque) {
            this.form.fecha_zarpe = this.formatDate(desatraque);
            this.form.hora_zarpe = this.formatTime(desatraque);
          } else {
            this.form.fecha_zarpe = "";
            this.form.hora_zarpe = "";
          }

          // Validación de negocio: requiere fechas completas para proceder
          if (!atraque || !desatraque) {
            this.bloqueado = true;
            this.bloqueoMensaje =
              "La solicitud seleccionada no cuenta con fechas completas de atraque y/o desatraque. No es posible continuar.";
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    verDilaogConfirmPago(_id) {
      this._idCna = _id;
      this.dialogConfirmarPago = true;
    },

    confirmarPago() {
      this.dialogConfirmarPago = false;
      this.confirmacionExtra = false;
      this.loading = true;
      actualizarPagoCna(this._idCna)
        .then(() => {
          this.snackbar.show = true;
          this.snackbar.text = "Pago confirmado correctamente";
          this.snackbar.color = "success";
          this._idCna = "";
          this.obtenerSolicitudesConCna();
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async imprimirCna(datos, item) {
      oficio_cna(datos, item);
    },

    formatDate(iso) {
      // Devuelve YYYY-MM-DD
      try {
        const d = new Date(iso);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      } catch (e) {
        return "";
      }
    },
    formatTime(iso) {
      // Devuelve HH:mm
      try {
        const d = new Date(iso);
        const hh = String(d.getHours()).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${hh}:${mm}`;
      } catch (e) {
        return "";
      }
    },
    formatDateTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day} ${hh}:${mm}`;
    },

    // Formatea el folio según las reglas de negocio del puerto (localización y tipo de muelle)
    formatFolio(folio, muelle, created) {
      const folio_number = folio.toString().padStart(4, "0");
      const date = new Date(created);
      const day = date.getUTCDate().toString().padStart(2, "0"); // Asegura dos dígitos
      const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = date.getUTCFullYear();
      
      // Determina la zona del muelle (Chiltepec o Frontera)
      let muelle_folio = "";
      if (
        muelle == "CHILTEPEC" ||
        muelle == "MUELLE DE TANQUES" ||
        muelle == "MUELLE PESCADORES CHILTEPEC"
      ) {
        muelle_folio = "CH";
      } else {
        muelle_folio = "FR";
      }
      
      // Mapea cada muelle a su clave específica
      let clave_muelle = "";
      switch (muelle) {
        case "ESPADAÑAL":
          clave_muelle = "PAR";
          break;
        case "CENTRO DE REPARACIONES NAVALES":
          clave_muelle = "PAR";
          break;
        case "MUELLE PESCADORES FRONTERA":
          clave_muelle = "DEP";
          break;
        case "MUELLE FISCAL":
          clave_muelle = "FIS";
          break;
        case "PARTICULAR":
          clave_muelle = "PAR";
          break;
        case "MUELLE DE TANQUES":
          clave_muelle = "PAR";
          break;
        case "MUELLE PESCADORES CHILTEPEC":
          clave_muelle = "PES";
          break;
        case "SANCHEZ MAGALLANES":
          clave_muelle = "PAR";
          break;
        case "MUELLE DE TANQUES":
          clave_muelle = "PAR";
          break;
        case "MUELLE COSUGO":
          clave_muelle = "PAR";
          break;
        case "MUELLE PRIVADO CARLOS DAVID":
          clave_muelle = "PAR";
          break;
      }

      // Lógica especial para folios <= 75 (formato simplificado)
      if (folio <= 75) {
        return folio_number;
      }
      
      // Para folios > 75, aplica formato completo con localización y fecha
      let otro_folio = folio_number - 75;
      const folio_number_2 = otro_folio.toString().padStart(4, "0");
      return `${folio_number_2}/${muelle_folio}/${clave_muelle}/${day}${month}${year}`;
    },

    formatDateDisplay(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES");
    },

    cambiarPagina(nuevaPagina) {
      this.page = nuevaPagina;
      this.aplicarFiltros();
    },

    cambiarLimite(nuevoLimite) {
      this.limit = nuevoLimite;
      this.page = 1;
      this.aplicarFiltros();
    },
  },
  computed: {
    isValidForm() {
      return (
        !!this.form.solicitud &&
        !!this.form.tipo &&
        !!this.form.puerto_destino &&
        !!this.form.fecha_arribo &&
        !!this.form.hora_arribo &&
        !!this.form.fecha_zarpe &&
        !!this.form.hora_zarpe
      );
    },
    fechaTexto() {
      if (!this.fechaInicio && !this.fechaFin) {
        return "";
      }
      if (this.fechaInicio && this.fechaFin) {
        if (this.fechaInicio === this.fechaFin) {
          return this.formatDateDisplay(this.fechaInicio);
        }
        return `${this.formatDateDisplay(this.fechaInicio)} - ${this.formatDateDisplay(
          this.fechaFin
        )}`;
      }
      return this.formatDateDisplay(this.fechaInicio || this.fechaFin);
    },
    // Transforma los datos de solicitudes para mostrar en la tabla con formato apropiado
    solicitudesTabla() {
      return (this.solicitudesCna || []).map((s) => ({
        _id: s._id,
        folio_solicitud: this.formatFolio(s.folio_solicitud, s.muelle, s.createdAt),
        solicitante: s.usuario[0].nombre || "Solicitante demo",
        fecha: s.createdAt,
        muelle: s.muelle,
        oficios: (s.cna && s.cna.length) || 0,
        embarcacion: s.embarcacion[0],
        cna: s.cna || [],
        agencia: s.agencia[0],
        puerto_procedencia: s.procedencia,
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
.subtitle {
  font-size: 14px;
}

.filter-card {
  background: #f9fbff;
  border-color: #e3f2fd !important;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.table-color .v-data-table-header {
  background-color: #7c1c44; /* Fondo verde */
  color: white; /* Color del texto */
}

.table-header {
  background-color: #7e7e7e; /* azul Vuetify primary */
  color: black;
}
</style>
